// ────────────────────────────────────────────────────────────────────────────
//  Profile.tsx — Profil + Einstellungen, voll i18n + alle Sektionen
//
//  Sektionen:
//   - Account-Header (Avatar)
//   - Persönliche Daten (Vorname, Nachname)
//   - Erscheinung (Theme)
//   - Sprache
//   - Sicherheit (Passwort, E-Mail)
//   - Aktive Abos (Plattform-Badge + Cancel-Hinweis)
//   - Organisation (wenn zugewiesen)
//   - Danger-Zone
// ────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../lib/useAuth'
import { ThemeSegmented, useThemeChoice } from '../components/AppShell'
import { setThemeChoice } from '../lib/theme'
import {
  changePassword,
  requestEmailChange,
  confirmEmailChange,
  updateLanguage,
  updateName,
  uploadAvatar,
  deleteAvatar,
  type AppUser,
} from '../lib/auth'
import { useExtensions, type Extension } from '../lib/useExtensions'
import { useT, useLangCode, setLang, LANGS, type LangCode } from '../i18n'

export function Profile() {
  const { user, setAuthUser } = useAuth()
  const t = useT()
  if (!user) return null

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12 space-y-5">
      <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('profile.title')}</h1>
      <p className="text-app-text-dim mb-8">{t('profile.subtitle')}</p>

      <AccountCard user={user} setAuthUser={setAuthUser} />
      <PersonalCard user={user} setAuthUser={setAuthUser} />
      <AppearanceCard />
      <LanguageCard />
      <SubscriptionCard />
      <OrganisationCard user={user} />
      <SecurityCard userEmail={user.email} setAuthUser={setAuthUser} />
      <DangerZone />
    </div>
  )
}

// ── Account-Card (Avatar + Status-Pills) ────────────────────────────────────
function AccountCard({ user, setAuthUser }: { user: AppUser; setAuthUser: (u: AppUser) => void }) {
  const t = useT()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const initials = (user.displayName || user.email || '?')
    .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setBusy(true); setErr(null)
    try {
      const u = await uploadAvatar(f)
      setAuthUser(u)
    } catch (e: any) {
      setErr(e?.message ?? 'Upload fehlgeschlagen.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }
  const handleRemove = async () => {
    if (!user.avatarUrl) return
    if (!confirm(t('profile.avatarRemoveQ'))) return
    setBusy(true); setErr(null)
    try { setAuthUser(await deleteAvatar()) }
    catch (e: any) { setErr(e?.message ?? 'Fehler.') }
    finally { setBusy(false) }
  }

  return (
    <Card>
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div className="relative shrink-0">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl.startsWith('http') ? user.avatarUrl : `https://custosoft-api.davidschroedinger.workers.dev${user.avatarUrl}`}
              alt=""
              className="w-20 h-20 rounded-3xl object-cover"
              style={{ border: '1px solid var(--border-strong)' }}
            />
          ) : (
            <div
              className="w-20 h-20 rounded-3xl grid place-items-center text-2xl font-bold text-white bg-gradient-to-br from-accent to-accent2"
              style={{ border: '1px solid var(--border-strong)' }}
            >
              {initials || '?'}
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 grid place-items-center rounded-3xl bg-black/40 backdrop-blur-sm">
              <span className="w-6 h-6 border-2 rounded-full border-white border-t-transparent animate-spin" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold truncate">{user.displayName || '–'}</h2>
          <div className="text-app-text-dim text-sm mt-0.5 truncate">{user.email}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge color="accent">{user.accountType === 'Organisation' ? t('profile.typeOrg') : t('profile.typePrivate')}</Badge>
            {user.appRole && <Badge color="accent2">🛡️ {user.appRole}</Badge>}
            {user.emailConfirmed
              ? <Badge color="teal">{t('profile.emailVerified')}</Badge>
              : <Badge color="coral">{t('profile.emailUnverified')}</Badge>}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-ghost text-sm py-2 px-4 disabled:opacity-50">
              {t('profile.avatarChange')}
            </button>
            {user.avatarUrl && (
              <button onClick={handleRemove} disabled={busy} className="btn-ghost text-sm py-2 px-4 disabled:opacity-50" style={{ color: 'var(--text-dim)' }}>
                {t('profile.avatarRemove')}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
          </div>

          {err && <div className="mt-3 text-sm text-coral">{err}</div>}
        </div>
      </div>
    </Card>
  )
}

// ── Persönliche Daten (Name) ────────────────────────────────────────────────
function PersonalCard({ user, setAuthUser }: { user: AppUser; setAuthUser: (u: AppUser) => void }) {
  const t = useT()
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // initial aus user.displayName splitten (Worker schickt es zusammengesetzt)
  useEffect(() => {
    const dn = user.displayName ?? ''
    const parts = dn.trim().split(/\s+/)
    setFirst(parts[0] ?? '')
    setLast(parts.slice(1).join(' '))
  }, [user.displayName])

  const dirty = (() => {
    const dn = (user.displayName ?? '').trim()
    return `${first} ${last}`.replace(/\s+/g, ' ').trim() !== dn
  })()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null); setSaved(false)
    try {
      const u = await updateName(first.trim(), last.trim())
      setAuthUser(u); setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e: any) {
      setErr(e?.message ?? 'Fehler.')
    } finally { setBusy(false) }
  }

  return (
    <Card title={t('profile.accountTitle')} subtitle={t('profile.accountSubtitle')} icon="👤">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <FormField label={t('profile.firstName')}>
            <input type="text" value={first} onChange={e => setFirst(e.target.value)} maxLength={60} className="input-base" />
          </FormField>
          <FormField label={t('profile.lastName')}>
            <input type="text" value={last} onChange={e => setLast(e.target.value)} maxLength={60} className="input-base" />
          </FormField>
        </div>
        {err && <div className="text-sm text-coral">{err}</div>}
        {saved && <div className="text-sm text-teal">{t('common.saved')}</div>}
        <div className="flex justify-end">
          <button type="submit" disabled={busy || !dirty} className="btn-primary text-sm py-2.5 px-5 disabled:opacity-40">
            {busy ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </Card>
  )
}

// ── Appearance ──────────────────────────────────────────────────────────────
function AppearanceCard() {
  const t = useT()
  const choice = useThemeChoice()
  return (
    <Card title={t('profile.appearanceTitle')} icon="🎨" subtitle={t('profile.appearanceSub')}>
      <ThemeSegmented value={choice} onChange={setThemeChoice} />
      <p className="mt-3 text-xs text-app-text-mute">{t('profile.appearanceNote')}</p>
    </Card>
  )
}

// ── Sprache ─────────────────────────────────────────────────────────────────
function LanguageCard() {
  const t = useT()
  const cur = useLangCode()
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const set = async (l: LangCode) => {
    if (l === cur) return
    setSaving(true); setSaved(false)
    setLang(l)
    try {
      await updateLanguage(l)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch { /* lokal trotzdem gespeichert */ }
    finally { setSaving(false) }
  }

  return (
    <Card title={t('profile.languageTitle')} icon="🌐" subtitle={t('profile.languageSub')}>
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl"
           style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
        {(Object.keys(LANGS) as LangCode[]).map(code => {
          const opt = LANGS[code]
          const active = cur === code
          return (
            <button
              key={code}
              onClick={() => set(code)}
              disabled={saving}
              className={`py-2.5 rounded-lg transition text-sm font-medium ${
                active ? 'bg-accent text-white shadow-md shadow-accent/30'
                       : 'text-app-text-dim hover:bg-app-elev hover:text-app-text'
              }`}
            >
              {opt.flag} {opt.label}
            </button>
          )
        })}
      </div>
      {saved && <div className="mt-2 text-xs text-teal">{t('common.saved')}</div>}
    </Card>
  )
}

// ── Subscription / Abos ─────────────────────────────────────────────────────
function SubscriptionCard() {
  const t = useT()
  const { items, loading } = useExtensions()
  const lang = useLangCode()

  // Worker liefert grantedVia + platform:
  //   - grantedVia: 'Purchase' (eigener Kauf) | 'OrgMembership' (von Owner geerbt)
  //   - platform:   'apple' (Apple-IAP) | 'web' (Stripe etc.) | null (unbekannt)
  // Eigene Käufe ohne Plattform-Markierung → standardmäßig Apple (historisch
  // alle App-Käufe via iOS).
  const platformOf = (e: Extension): 'apple' | 'web' | 'org' => {
    if (e.grantedVia === 'OrgMembership') return 'org'
    return e.platform === 'web' ? 'web' : 'apple'
  }

  return (
    <Card title={t('profile.subsTitle')} icon="💎" subtitle={t('profile.subsSub')}>
      {loading ? (
        <div className="text-sm text-app-text-mute">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-6">
          <div className="text-4xl mb-2 opacity-40">💎</div>
          <div className="text-sm text-app-text-dim">{t('profile.subsNone')}</div>
          <a
            href="https://apps.apple.com/app/id6766051168"
            target="_blank" rel="noreferrer"
            className="inline-block mt-3 text-sm font-medium text-accent hover:underline"
          >
            {t('profile.subsBuy')}
          </a>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {items.map(e => {
            const platform = platformOf(e)
            return (
              <li key={e.id} className="rounded-xl p-3.5 glass-flat flex flex-wrap items-center gap-3">
                <div className="grid place-items-center w-10 h-10 rounded-xl bg-accent/15 border border-accent/25 text-lg">
                  {productIcon(e.product)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{productLabel(e.product, lang)}</div>
                  <div className="text-xs text-app-text-mute mt-0.5">
                    {e.purchasedAt && t('profile.subsActiveSince', { date: new Date(e.purchasedAt).toLocaleDateString(lang) })}
                    {e.expiresAt && <> · {t('profile.subsExpiresAt', { date: new Date(e.expiresAt).toLocaleDateString(lang) })}</>}
                  </div>
                </div>
                <PlatformBadge platform={platform} />
                {platform === 'apple' && (
                  <a
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank" rel="noreferrer"
                    className="text-[11px] font-medium text-app-text-dim hover:text-coral underline decoration-app-border-2 hover:decoration-coral underline-offset-2 transition shrink-0"
                  >
                    {t('profile.subsCancelApple')}
                  </a>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {items.some(e => e.grantedVia === 'Purchase' && (e.platform ?? 'apple') === 'apple') && (
        <p className="mt-4 text-[11px] text-app-text-mute">
          {t('profile.subsAppleHint')}
        </p>
      )}
    </Card>
  )
}

function PlatformBadge({ platform }: { platform: 'apple' | 'web' | 'org' }) {
  const t = useT()
  const map = {
    apple: { txt: t('profile.subsViaApple'), bg: 'rgba(255,255,255,0.10)', border: 'var(--border-strong)', color: 'var(--text)' },
    web:   { txt: t('profile.subsViaWeb'),   bg: 'rgba(58,98,255,0.15)',   border: 'rgba(58,98,255,0.30)', color: '#3a62ff' },
    org:   { txt: t('profile.subsViaOrg'),   bg: 'rgba(51,214,112,0.12)',  border: 'rgba(51,214,112,0.30)', color: '#33d670' },
  }[platform]
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border shrink-0"
      style={{ background: map.bg, color: map.color, borderColor: map.border }}
    >
      {map.txt}
    </span>
  )
}

function productIcon(slug: string): string {
  const m: Record<string, string> = {
    PunchClock:          '🕐',
    TerminalMode:        '🖥️',
    Recruitment:         '📋',
    BusinessBasic:       '🏢',
    BusinessL:           '🏢',
    BusinessBasicYearly: '🏢',
    BusinessLYearly:     '🏢',
  }
  return m[slug] ?? '📦'
}
function productLabel(slug: string, lang: LangCode): string {
  // De/EN Labels für die wichtigsten Produkte. Fallback = Slug.
  const labels: Record<string, { de: string; en: string }> = {
    PunchClock:          { de: 'Stempeluhr',          en: 'Time clock' },
    TerminalMode:        { de: 'Wand-Stempeluhr',     en: 'Wall time clock' },
    Recruitment:         { de: 'Bewerbungsmanager',   en: 'Recruitment' },
    BusinessBasic:       { de: 'Business Basic',      en: 'Business Basic' },
    BusinessL:           { de: 'Business L',          en: 'Business L' },
    BusinessBasicYearly: { de: 'Business Basic (Jahr)', en: 'Business Basic (Yearly)' },
    BusinessLYearly:     { de: 'Business L (Jahr)',     en: 'Business L (Yearly)' },
  }
  const l = labels[slug]
  return l ? l[lang] : slug
}

// ── Organisation ────────────────────────────────────────────────────────────
function OrganisationCard({ user }: { user: AppUser }) {
  const t = useT()
  if (!user.orgId) {
    return (
      <Card title={t('profile.orgTitle')} icon="🏢" subtitle={t('profile.orgSub')}>
        <div className="text-sm text-app-text-mute py-2">{t('profile.orgNone')}</div>
      </Card>
    )
  }
  return (
    <Card title={t('profile.orgTitle')} icon="🏢" subtitle={t('profile.orgSub')}>
      <div className="flex items-start gap-3">
        <div className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-brandBlue/30 to-accent/30 text-xl">
          🏢
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">Org #{user.orgId}</div>
          {user.orgRole && (
            <div className="text-xs text-app-text-mute mt-0.5">
              {t('profile.orgRole')}: <strong>{user.orgRole}</strong>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// ── Sicherheit ──────────────────────────────────────────────────────────────
function SecurityCard({ userEmail, setAuthUser }: { userEmail: string; setAuthUser: (u: AppUser) => void }) {
  const t = useT()
  return (
    <Card title={t('profile.securityTitle')} icon="🔒" subtitle={t('profile.securitySub')}>
      <PasswordChange />
      <div className="my-5 h-px" style={{ background: 'var(--border)' }} />
      <EmailChange currentEmail={userEmail} setAuthUser={setAuthUser} />
    </Card>
  )
}

function PasswordChange() {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [cur, setCur] = useState('')
  const [nw, setNw] = useState('')
  const [nw2, setNw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null)
    if (nw.length < 8) { setErr(t('auth.minLength')); return }
    if (nw !== nw2)    { setErr(t('auth.notMatching')); return }
    setBusy(true)
    try {
      await changePassword(cur, nw)
      setOk(true); setCur(''); setNw(''); setNw2('')
      setTimeout(() => { setOk(false); setOpen(false) }, 2000)
    } catch (e: any) {
      setErr(e?.message ?? t('common.error'))
    } finally { setBusy(false) }
  }

  if (!open) return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="font-medium text-sm">{t('profile.pwLabel')}</div>
        <div className="text-xs text-app-text-mute">{t('profile.pwLastChanged')}</div>
      </div>
      <button onClick={() => setOpen(true)} className="btn-ghost text-sm py-2 px-4">{t('common.change')}</button>
    </div>
  )
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="font-medium text-sm">{t('profile.pwChangeTitle')}</div>
      <input type="password" required value={cur} onChange={e => setCur(e.target.value)} placeholder={t('auth.currentPassword')} autoComplete="current-password" className="input-base" />
      <input type="password" required minLength={8} value={nw} onChange={e => setNw(e.target.value)} placeholder={t('auth.newPassword')} autoComplete="new-password" className="input-base" />
      <input type="password" required minLength={8} value={nw2} onChange={e => setNw2(e.target.value)} placeholder={t('auth.repeatPassword')} autoComplete="new-password" className="input-base" />
      {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
      {ok  && <div className="text-sm text-teal bg-teal/10 border border-teal/30 rounded-lg p-2.5">{t('profile.pwOk')}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-primary text-sm py-2.5 px-4 disabled:opacity-50">{busy ? t('common.saving') : t('common.save')}</button>
        <button type="button" onClick={() => { setOpen(false); setErr(null) }} className="btn-ghost text-sm py-2 px-4">{t('common.cancel')}</button>
      </div>
    </form>
  )
}

function EmailChange({ currentEmail, setAuthUser }: { currentEmail: string; setAuthUser: (u: AppUser) => void }) {
  const t = useT()
  const [phase, setPhase] = useState<'idle' | 'request' | 'confirm'>('idle')
  const [newEmail, setNewEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null)
    try { await requestEmailChange(newEmail.trim()); setPhase('confirm') }
    catch (e: any) { setErr(e?.message ?? t('common.error')) }
    finally { setBusy(false) }
  }
  const confirm = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null)
    try {
      const u = await confirmEmailChange(newEmail.trim(), code.trim())
      setAuthUser(u); setPhase('idle'); setNewEmail(''); setCode('')
    } catch (e: any) { setErr(e?.message ?? t('profile.codeInvalid')) }
    finally { setBusy(false) }
  }

  if (phase === 'idle') return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium text-sm">{t('profile.emailAddress')}</div>
        <div className="text-xs text-app-text-mute truncate">{currentEmail}</div>
      </div>
      <button onClick={() => setPhase('request')} className="btn-ghost text-sm py-2 px-4 shrink-0">{t('common.change')}</button>
    </div>
  )
  if (phase === 'request') return (
    <form onSubmit={requestCode} className="space-y-3">
      <div className="font-medium text-sm">{t('profile.newEmailLabel')}</div>
      <input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="neu@example.com" autoComplete="email" className="input-base" />
      <p className="text-xs text-app-text-mute">{t('profile.codeSentHint')}</p>
      {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-primary text-sm py-2.5 px-4 disabled:opacity-50">{busy ? '…' : t('profile.sendCode')}</button>
        <button type="button" onClick={() => { setPhase('idle'); setErr(null) }} className="btn-ghost text-sm py-2 px-4">{t('common.cancel')}</button>
      </div>
    </form>
  )
  return (
    <form onSubmit={confirm} className="space-y-3">
      <div className="font-medium text-sm">{t('profile.enterCode')}</div>
      <div className="text-xs text-app-text-mute">{t('profile.codeSentTo', { email: newEmail })}</div>
      <input type="text" inputMode="numeric" maxLength={6} required value={code} onChange={e => setCode(e.target.value)} placeholder="123456" className="input-base text-center text-2xl font-mono tracking-widest" />
      {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-primary text-sm py-2.5 px-4 disabled:opacity-50">{busy ? '…' : t('common.confirm')}</button>
        <button type="button" onClick={() => setPhase('request')} className="btn-ghost text-sm py-2 px-4">{t('profile.otherEmail')}</button>
      </div>
    </form>
  )
}

// ── Danger ──────────────────────────────────────────────────────────────────
function DangerZone() {
  const t = useT()
  const supportEmail = 'taironic.media@gmail.com'
  return (
    <div
      className="rounded-3xl p-5 md:p-7"
      style={{ background: 'rgba(255,91,92,0.04)', border: '1px solid rgba(255,91,92,0.25)' }}
    >
      <h3 className="font-semibold text-coral flex items-center gap-2">{t('profile.deleteTitle')}</h3>
      <p className="mt-1 text-sm text-app-text-dim">
        {t('profile.deleteText', { email: supportEmail }).split(supportEmail).map((part, i, arr) => (
          <span key={i}>
            {part}
            {i < arr.length - 1 && (
              <a href={`mailto:${supportEmail}`} className="underline decoration-coral/40 hover:decoration-coral">{supportEmail}</a>
            )}
          </span>
        ))}
      </p>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Card(props: { title?: string; subtitle?: string; icon?: string; children: React.ReactNode }) {
  return (
    <section className="glass p-5 md:p-7">
      {props.title && (
        <header className="mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            {props.icon && <span className="text-lg">{props.icon}</span>}
            {props.title}
          </h3>
          {props.subtitle && <p className="text-xs text-app-text-mute mt-0.5">{props.subtitle}</p>}
        </header>
      )}
      {props.children}
    </section>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">{label}</span>
      {children}
    </label>
  )
}

function Badge({ color, children }: { color: 'accent' | 'accent2' | 'teal' | 'coral'; children: React.ReactNode }) {
  const styles = {
    accent:  { bg: 'rgba(138,68,238,0.15)', text: '#8a44ee', border: 'rgba(138,68,238,0.30)' },
    accent2: { bg: 'rgba(100,50,200,0.15)', text: '#8a44ee', border: 'rgba(100,50,200,0.30)' },
    teal:    { bg: 'rgba(51,214,112,0.12)', text: '#33d670', border: 'rgba(51,214,112,0.30)' },
    coral:   { bg: 'rgba(255,91,92,0.12)',  text: '#ff5b5c', border: 'rgba(255,91,92,0.30)' },
  }[color]
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border"
      style={{ background: styles.bg, color: styles.text, borderColor: styles.border }}
    >
      {children}
    </span>
  )
}
